/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * TableDetailsModal Component
 * ---------------------------
 * A full-screen, theme-aware modal for displaying an application's
 * table definition in a clean, scrollable, and developer-friendly layout.
 *
 * Features:
 * • Click-outside backdrop close + dedicated close button
 * • Responsive centered modal with overflow handling
 * • Displays complete field metadata:
 *      – Field name
 *      – Data type
 *      – Max length
 *      – Numeric precision & scale
 *      – Required flag
 *      – Related table reference
 *      – Display name & hint
 * • Automatically hides when no table is provided
 *
 * Props:
 * - theme: Theme object for colors, accents, and radius
 * - table: Table schema object (name, display_name, fields[])
 * - onClose: Callback to close the modal
 *
 * Purpose:
 * Used primarily inside schema builders or admin dashboards to quickly
 * inspect table structures without navigating away from the main page.
 */
import React, { useState, useEffect } from 'react';

const TableDetailsModal = ({ theme, table, onClose }) => {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 991);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 991);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    if (!table) return null;

    const { name, fields } = table;

    return (
        <div
            style={{
                position: 'fixed',
                top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                zIndex: 3000,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: isMobile ? '0.5rem' : '1rem',
                overflow: 'auto',
            }}
            onClick={onClose}
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="table-details-title"
                onClick={e => e.stopPropagation()}
                style={{
                    background: theme.footer,
                    borderRadius: theme.cardRadius,
                    width: isMobile ? '98%' : '90%',
                    maxWidth: '900px',
                    maxHeight: '90vh',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    boxShadow: `0 8px 32px rgba(0,0,0,0.2)`,
                    padding: isMobile ? '1rem' : '1.5rem',
                    color: theme.text,
                    fontFamily: 'system-ui, sans-serif',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '1rem',
                        position: 'relative',
                    }}
                >
                    <h2 id="table-details-title" style={{ margin: 0, fontWeight: 700, fontSize: isMobile ? '1.25rem' : '1.5rem', color: theme.primary, wordBreak: 'break-all' }}>
                        Table: {table.display_name || name}
                    </h2>
                    <button
                        onClick={onClose}
                        aria-label="Close"
                        style={{
                            border: 'none',
                            background: 'none',
                            fontSize: '1.7rem',
                            fontWeight: 'bold',
                            color: theme.secondary,
                            cursor: 'pointer',
                            marginLeft: '10px'
                        }}
                    >
                        &times;
                    </button>
                </div>

                {/* Desktop View Table > 991px */}
                <div className="d-none d-lg-block" style={{ flex: 1, overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
                        <thead>
                            <tr>
                                <th style={{ borderBottom: `2px solid ${theme.secondary}`, padding: '0.5rem', textAlign: 'left' }}>Field</th>
                                <th style={{ borderBottom: `2px solid ${theme.secondary}`, padding: '0.5rem', textAlign: 'left' }}>Data Type</th>
                                <th style={{ borderBottom: `2px solid ${theme.secondary}`, padding: '0.5rem', textAlign: 'left' }}>Max Length</th>
                                <th style={{ borderBottom: `2px solid ${theme.secondary}`, padding: '0.5rem', textAlign: 'left' }}>Precision</th>
                                <th style={{ borderBottom: `2px solid ${theme.secondary}`, padding: '0.5rem', textAlign: 'left' }}>Scale</th>
                                <th style={{ borderBottom: `2px solid ${theme.secondary}`, padding: '0.5rem', textAlign: 'left' }}>Required</th>
                                <th style={{ borderBottom: `2px solid ${theme.secondary}`, padding: '0.5rem', textAlign: 'left' }}>Unique</th>
                                <th style={{ borderBottom: `2px solid ${theme.secondary}`, padding: '0.5rem', textAlign: 'left' }}>Related Table</th>
                                <th style={{ borderBottom: `2px solid ${theme.secondary}`, padding: '0.5rem', textAlign: 'left' }}>Display Name</th>
                                <th style={{ borderBottom: `2px solid ${theme.secondary}`, padding: '0.5rem', textAlign: 'left' }}>Hint</th>
                            </tr>
                        </thead>
                        <tbody>
                            {fields.length === 0 ? (
                                <tr>
                                    <td colSpan={9} style={{ padding: '1rem', textAlign: 'center', color: theme.secondary }}>
                                        Table has no fields.
                                    </td>
                                </tr>
                            ) : (
                                fields.map((field, i) => (
                                    <tr key={i} style={{ borderBottom: i === fields.length - 1 ? 'none' : `1px solid ${theme.primaryLight}80` }}>
                                        <td style={{ padding: '0.5rem', fontWeight: 500 }}>{field.name}</td>
                                        <td style={{ padding: '0.5rem' }}>{field.data_type}</td>
                                        <td style={{ padding: '0.5rem' }}>{field.max_length ?? '-'}</td>
                                        <td style={{ padding: '0.5rem' }}>{field.numeric_precision ?? '-'}</td>
                                        <td style={{ padding: '0.5rem' }}>{field.numeric_scale ?? '-'}</td>
                                        <td style={{ padding: '0.5rem' }}>{field.required ? 'Yes' : 'No'}</td>
                                        <td style={{ padding: '0.5rem' }}>{field.is_unique ? 'Yes' : 'No'}</td>
                                        <td style={{ padding: '0.5rem' }}>{field.related_table_name ?? '-'}</td>
                                        <td style={{ padding: '0.5rem' }}>{field.display_name ?? '-'}</td>
                                        <td style={{ padding: '0.5rem' }}>{field.hint ?? '-'}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Mobile View Stacked Cards <= 991px */}
                <div className="d-lg-none">
                    {fields.length === 0 ? (
                        <div style={{ padding: '1rem', textAlign: 'center', color: theme.secondary }}>
                            Table has no fields.
                        </div>
                    ) : (
                        <div className="d-flex flex-column gap-3">
                            {fields.map((field, i) => (
                                <div key={i} style={{
                                    background: '#fff',
                                    border: `1px solid ${theme.primary}20`,
                                    borderRadius: theme.cardRadius,
                                    padding: '1rem',
                                    position: 'relative'
                                }}>
                                    <div className="d-flex justify-content-between mb-2 pb-2 border-bottom">
                                        <div style={{ fontWeight: 700, color: theme.primary, fontSize: '1.1rem' }}>
                                            {field.name}
                                        </div>
                                        <span className="badge bg-light text-dark border">
                                            {field.data_type}
                                        </span>
                                    </div>

                                    <div className="d-flex flex-column gap-1" style={{ fontSize: '0.9rem' }}>
                                        <div className="d-flex justify-content-between">
                                            <span className="text-muted">Required:</span>
                                            <span style={{ fontWeight: 500 }}>{field.required ? 'Yes' : 'No'}</span>
                                        </div>
                                        {field.max_length && (
                                            <div className="d-flex justify-content-between">
                                                <span className="text-muted">Max Length:</span>
                                                <span>{field.max_length}</span>
                                            </div>
                                        )}
                                        {field.related_table_name && (
                                            <div className="d-flex justify-content-between">
                                                <span className="text-muted">Related:</span>
                                                <span style={{ color: theme.primary }}>{field.related_table_name}</span>
                                            </div>
                                        )}
                                        <div className="mt-1 pt-1 border-top">
                                            <div style={{ fontSize: '0.8rem', textTransform: 'uppercase', color: '#888', fontWeight: 600 }}>Display</div>
                                            <div>{field.display_name || '-'}</div>
                                        </div>
                                        {field.hint && (
                                            <div>
                                                <div style={{ fontSize: '0.8rem', textTransform: 'uppercase', color: '#888', fontWeight: 600 }}>Hint</div>
                                                <div style={{ fontStyle: 'italic', color: '#555' }}>{field.hint}</div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
};

export default TableDetailsModal;
